import { MainContainer } from '@/components';
import { DisclosureCard } from '@/components/Disclosures/DisclosureCard';
import { createApplicationRequest } from '@/helpers/createApplicationRequest';
import { creditCheckTranslations } from '@/helpers/creditCheckTranslations';
import { isNextEnvironment } from '@/helpers/isNextEnvironment';
import usePermissions from '@/hooks/usePermissions';
import { useTranslation } from '@/hooks/useTranslation';
import { useEditAccountMutation, useGetAccountWithTokenQuery } from '@/services/accounts';
import { useCreateApplicationMutation, useGetApplicationsQuery } from '@/services/applications';
import { useGetDisclosuresByTypeQuery } from '@/services/disclosures';
import { isErrorWithData } from '@/services/helpers';
import type { TurnstileInstance } from '@marsidev/react-turnstile';
import { Box, Button, Modal, toast, Toggle, useOpenClose } from '@palmetto/palmetto-components';
import { Formik } from 'formik';
import { useFlags } from 'launchdarkly-react-client-sdk';
import isEqual from 'lodash/isEqual';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate, useParams } from 'react-router-dom';
import * as yup from 'yup';

import {
  ApplicantType,
  ApplicationDocumentWithAdditionalApplicantData,
  DisclosureType,
  FinancingDecision,
  Locale,
  ProgramType,
  strictCreditCheckProgramTypes,
  UnderwritingNotes,
  UserPermissions,
} from 'types';

import { AddressFormFields } from '../AddressFormFields/AddressFormFields';
import { useIsOrgReadOnly } from '../auth/claimsUtils';
import { useInviteToken } from '../auth/useInviteToken';
import { EnhancedFormikForm } from '../EnhancedFormikForm';
import FormSection from '../Forms/FormSection';
import PageHeader from '../PageHeader';

import { ApplicantInformation } from './ApplicantInformation';
import { ApplicationPreferredLanguage } from './ApplicationPreferredLanguage';
import { TurnstileWidget } from './TurnstileWidget';
interface ApplicantSchema {
  firstName: string;
  middleName?: string | null;
  lastName: string;
  phoneNumber: string;
  email: string;
  ssn: string;
  dob?: string | null;
}

export interface ApplicationFormSchema {
  addAdditionalApplicant?: boolean | null;
  primary: ApplicantSchema;
  secondary?: ApplicantSchema;
  address1: string;
  address2?: string | null;
  city: string;
  state: string;
  zip: string;
  creditApplicationDisclosure: boolean;
}

export const getSsnRequirementsByProgramType = (programType?: ProgramType) => {
  switch (programType) {
    case ProgramType.consumerSolar:
      return {
        minLength: 9,
        maxLength: 9,
        label: 'socialSecurityNumber',
        helpText: 'nineDigitSsnHelpText',
        placeHolder: 'fullSsnExample',
        errorMessage: 'nineDigitSsnErrorMessage',
        matchingLength: /^[0-9]{9}$/,
      };
    case ProgramType.newHomes:
      return {
        minLength: 9,
        maxLength: 9,
        label: 'socialSecurityNumber',
        helpText: 'fullSsnRequired',
        placeHolder: 'fullSsnExample',
        errorMessage: 'ssnFullErrorMessage',
        matchingLength: /^[0-9]{9}$/,
      };
    default:
      return {
        minLength: 4,
        maxLength: 9,
        label: 'socialSecurityNumber',
        helpText: 'ssnDescription',
        placeHolder: 'ssnExample',
        errorMessage: 'ssnErrorMessage',
        matchingLength: /^[0-9]{9}$|^[0-9]{4}$/,
      };
  }
};

export const isDobRequiredForProgramType = (programType?: ProgramType): boolean =>
  programType !== undefined && strictCreditCheckProgramTypes.includes(programType);

export const isCoApplicantAllowedForProgramType = (programType?: ProgramType): boolean =>
  programType === undefined || !strictCreditCheckProgramTypes.includes(programType);

/**
 * This component is used both within our application and via invite token
 * Any API queries made here or in child components need to use the invite token otherwise it won't work accessing outside of our app
 */

const DEFAULT_COUNTDOWN_SECONDS = 0;
const REDIRECT_COUNTDOWN_SECONDS = 4;

export function ApplicationForm() {
  const { id } = useParams<{ id: any }>();
  const { inviteToken } = useInviteToken();
  const { comfortPlanDealerConditions, enableTurnstileOnCreditCheck = false } = useFlags();
  const { translate } = useTranslation({ useAccountPreference: true });
  const isInNextEnvironment = useMemo(() => isNextEnvironment(), []);
  const { data: accountData, isLoading: isAccountInfoLoading } = useGetAccountWithTokenQuery({
    id,
    inviteToken,
  });
  const [preferredLanguage, setPreferredLanguage] = useState<Locale>(accountData?.language || Locale.en);
  const propertyAddressNeeded = !accountData?.address?.address1 && accountData?.address?.state === 'PR';
  const { data: disclosures, isLoading: isDisclosuresLoading } = useGetDisclosuresByTypeQuery({
    type: DisclosureType.creditApplication,
    inviteToken,
    language: [preferredLanguage],
  });
  const { data: applications = [], isLoading: isApplicationLoading } = useGetApplicationsQuery({
    accountId: id,
    inviteToken,
  });
  const { isOpen: isCreditCheckSuccessModal, handleOpen: openCreditCheckSuccessModal } = useOpenClose();

  const [createApplication] = useCreateApplicationMutation();
  const [editAccount] = useEditAccountMutation();
  const isAllowedToExecuteAction = useIsOrgReadOnly();
  const navigate = useNavigate();
  const [showAdditionalApplicant, setShowAdditionalApplicant] = useState(false);
  const isLoading = isAccountInfoLoading || isDisclosuresLoading || isApplicationLoading;
  const { errorMessage, matchingLength } = getSsnRequirementsByProgramType(accountData?.programType);
  const userPermissions = usePermissions();
  const [countdown, setCountdown] = useState<number>(DEFAULT_COUNTDOWN_SECONDS);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const [turnstileError, setTurnstileError] = useState(false);
  const turnstileRef = useRef<TurnstileInstance | undefined>(undefined);

  const handleTurnstileSuccess = useCallback((token: string) => {
    setTurnstileToken(token);
    setTurnstileError(false);
  }, []);
  const handleTurnstileExpire = useCallback(() => setTurnstileToken(null), []);
  const handleTurnstileError = useCallback(() => {
    setTurnstileToken(null);
    setTurnstileError(true);
  }, []);
  useEffect(() => {
    if (countdown === DEFAULT_COUNTDOWN_SECONDS) return;

    const timer = setTimeout(() => {
      setCountdown(countdown - 1);
    }, 1000);
    if (countdown === 1) {
      navigate(`/accounts/${id}/system-design`);
    }

    return () => clearTimeout(timer);
  }, [countdown, id, navigate]);

  const handleStartCountdown = () => {
    setCountdown(REDIRECT_COUNTDOWN_SECONDS);
  };
  const userHasComfortPlanDealerPermission =
    comfortPlanDealerConditions && userPermissions.includes(UserPermissions.comfortPlanDealer);

  const applicantSchema = useMemo(
    () =>
      yup.object().shape({
        firstName: yup.string().required(translate('firstNameRequired', creditCheckTranslations, preferredLanguage)),
        middleName: yup.string().optional().nullable(),
        lastName: yup.string().required(translate('lastNameRequired', creditCheckTranslations, preferredLanguage)),
        phoneNumber: yup
          .string()
          .required(translate('phoneNumberRequired', creditCheckTranslations, preferredLanguage))
          .length(12, translate('phoneNumberLength', creditCheckTranslations, preferredLanguage)), // 10 digits + 2 for country code,
        email: yup
          .string()
          .email(translate('invalidEmail', creditCheckTranslations, preferredLanguage))
          .required(translate('emailRequired', creditCheckTranslations, preferredLanguage)),
        ssn: yup
          .string()
          .required(translate(errorMessage, creditCheckTranslations, preferredLanguage))
          .matches(matchingLength, translate(errorMessage, creditCheckTranslations, preferredLanguage))
          .test('invalid-ssn-pattern', function (ssnValue) {
            if (isInNextEnvironment) {
              return true;
            }

            const onlyNumericCharacters = ssnValue ? ssnValue.replace(/[^0-9]/g, '') : '';

            if (onlyNumericCharacters.length === 4) {
              const isAllZeros = onlyNumericCharacters === '0000';
              if (isAllZeros) {
                return this.createError({
                  message: translate('invalidSsnZeros', creditCheckTranslations, preferredLanguage),
                });
              }
              return true;
            }

            if (onlyNumericCharacters.length === 9) {
              const firstThreeDigits = onlyNumericCharacters.substring(0, 3);
              const middleTwoDigits = onlyNumericCharacters.substring(3, 5);
              const lastFourDigits = onlyNumericCharacters.substring(5, 9);

              const isProhibitedPattern =
                firstThreeDigits === '666' || middleTwoDigits === '00' || lastFourDigits === '0000';

              if (isProhibitedPattern) {
                return this.createError({
                  message: translate('invalidSsnPattern', creditCheckTranslations, preferredLanguage),
                });
              }
            }

            return true;
          }),
        dob: isDobRequiredForProgramType(accountData?.programType)
          ? yup.string().required(translate('dobRequired', creditCheckTranslations, preferredLanguage))
          : yup.string().optional().nullable(),
      }),
    [translate, errorMessage, matchingLength, isInNextEnvironment, preferredLanguage, accountData?.programType],
  );

  const applicationSchema = useMemo(
    () =>
      yup.object().shape({
        addAdditionalApplicant: yup.boolean().optional().nullable(),
        secondary: yup.object().when('addAdditionalApplicant', {
          is: true,
          then: () => applicantSchema,
        }),
        primary: applicantSchema,

        address1: yup
          .string()
          .required(translate('addressRequired', creditCheckTranslations, preferredLanguage))
          .max(60, 'Address Line 1 must be 60 characters or fewer.'),
        address2: yup.string().optional().nullable().max(60, 'Address Line 2 must be 60 characters or fewer.'),
        city: yup.string().required(translate('cityRequired', creditCheckTranslations, preferredLanguage)),
        state: yup.string().required(translate('stateRequired', creditCheckTranslations, preferredLanguage)),
        zip: yup
          .string()
          .required(translate('zipRequired', creditCheckTranslations, preferredLanguage))
          .matches(/^\d{5}$/, 'Zip Code must be exactly 5 digits.'),
        propertyAddress1: propertyAddressNeeded
          ? yup
              .string()
              .required(translate('addressRequired', creditCheckTranslations, preferredLanguage))
              .max(60, 'Address Line 1 must be 60 characters or fewer.')
          : yup.string().nullable().optional().max(60, 'Address Line 1 must be 60 characters or fewer.'),
        propertyAddress2: yup
          .string()
          .nullable()
          .optional()
          .max(60, 'Address Line 2 must be 60 characters or fewer.'),
        propertyCity: propertyAddressNeeded
          ? yup.string().required(translate('cityRequired', creditCheckTranslations))
          : yup.string().nullable().optional(),
        propertyState: propertyAddressNeeded
          ? yup.string().required(translate('stateRequired', creditCheckTranslations))
          : yup.string().nullable().optional(),
        propertyZip: propertyAddressNeeded
          ? yup
              .string()
              .required(translate('zipRequired', creditCheckTranslations))
              .matches(/^\d{5}$/, 'Zip Code must be exactly 5 digits.')
          : yup.string().nullable().optional(),
        creditApplicationDisclosure: yup
          .boolean()
          .oneOf(
            [true],
            translate('creditApplicationDisclosureRequired', creditCheckTranslations, preferredLanguage),
          )
          .required(),
      }),
    [applicantSchema, translate, preferredLanguage, propertyAddressNeeded],
  );

  const isCreditApproved = applications?.some(
    (app: ApplicationDocumentWithAdditionalApplicantData) => app.creditExpiryDate,
  );
  const preApprovedApplication = applications?.find(
    (app: ApplicationDocumentWithAdditionalApplicantData) =>
      app?.underwritingNotes?.length === 1 &&
      app?.underwritingNotes?.includes(UnderwritingNotes.preApproved) &&
      !isCreditApproved,
  );
  const preApprovedDisclosuresAcceptance = preApprovedApplication?.applicants
    ?.find((applicant) => applicant?.type === ApplicantType.primary)
    ?.disclosures?.find((disclosure) => disclosure?.type === DisclosureType.creditApplication)?.accepted;

  const handleSubmit = async (values: any, { setSubmitting }: any) => {
    try {
      const accountUpdates: any = {};
      if (preferredLanguage !== accountData?.language) {
        accountUpdates.language = preferredLanguage;
      }
      const address = {
        address1: values?.propertyAddress1?.trim() ?? '',
        address2: values?.propertyAddress2?.trim() ?? '',
        city: values?.propertyCity?.trim() ?? '',
        state: values?.propertyState ?? '',
        zip: values?.propertyZip?.trim() ?? '',
      };
      const hasAddressChanged = !isEqual(address, accountData?.address);
      if (hasAddressChanged) {
        accountUpdates.address = address;
      }

      const hasAnyAccountUpdates = Object.keys(accountUpdates).length > 0;
      if (hasAnyAccountUpdates) {
        await editAccount({
          account: accountUpdates,
          id,
          inviteToken,
        }).unwrap();
      }

      const { redirectUrl, status } = await createApplication({
        application: createApplicationRequest(accountData, disclosures, values),
        accountId: accountData?.id,
        inviteToken,
        turnstileToken: turnstileToken ?? undefined,
      }).unwrap();
      if (redirectUrl) {
        window.location.href = redirectUrl;
      } else {
        toast.success(translate('applicationSubmittedSuccess', creditCheckTranslations, preferredLanguage));
        setSubmitting(false);
        if (
          userHasComfortPlanDealerPermission &&
          !inviteToken &&
          [FinancingDecision.approved, FinancingDecision.approvedWithStipulations].includes(
            status as FinancingDecision,
          )
        ) {
          openCreditCheckSuccessModal();
          handleStartCountdown();
        } else {
          navigate(`/accounts/${id}`);
        }
      }
    } catch (e) {
      setSubmitting(false);
      setTurnstileToken(null);
      turnstileRef.current?.reset();
      if (isErrorWithData(e)) {
        const errorMessage = e.data.message;
        toast.error(errorMessage);
      } else {
        console.error(e);
        toast.error(translate('errorSubmittingApplication', creditCheckTranslations, preferredLanguage));
      }
    }
  };

  const initialValues = useMemo(() => {
    if (isLoading || !accountData) return;

    const initialApplicantValues: any = {
      primary: {
        firstName: '',
        middleName: '',
        lastName: '',
        email: '',
        phoneNumber: '',
        ssn: '',
        dob: '',
      },
      secondary: {
        firstName: '',
        middleName: '',
        lastName: '',
        email: '',
        phoneNumber: '',
        ssn: '',
        dob: '',
      },
    };
    accountData?.applicants?.forEach((applicant: any) => {
      initialApplicantValues[applicant.type] = {
        ...applicant,
        firstName: applicant.firstName,
        middleName: applicant.middleName,
        lastName: applicant.lastName,
        email: applicant.email,
        phoneNumber: applicant.phoneNumber,
        dob: '',
        ssn: '',
      };
    });

    if (
      accountData?.applicants &&
      accountData.applicants.length > 1 &&
      isCoApplicantAllowedForProgramType(accountData?.programType)
    ) {
      setShowAdditionalApplicant(true);
      initialApplicantValues.addAdditionalApplicant = true;
    }

    const propertyAddress = {
      propertyAddress1: accountData.address?.address1 || '',
      propertyAddress2: accountData.address?.address2 || '',
      propertyCity: accountData.address?.city || '',
      propertyState: accountData.address?.state || '',
      propertyZip: accountData.address?.zip || '',
    };
    return {
      ...initialApplicantValues['primary'].address,
      creditApplicationDisclosure: false,
      ...propertyAddress,
      ...initialApplicantValues,
      ...(preApprovedApplication
        ? {
            creditApplicationDisclosure: preApprovedDisclosuresAcceptance,
          }
        : {}),
      language: accountData?.language || Locale.en,
    };
  }, [accountData, isLoading, preApprovedApplication, preApprovedDisclosuresAcceptance]);

  useEffect(() => {
    if (accountData?.language && accountData.language !== preferredLanguage) {
      setPreferredLanguage(accountData.language);
    }
  }, [accountData?.language]);

  return (
    <>
      <Helmet>
        <title>{translate('creditCheckApplication', creditCheckTranslations, preferredLanguage)}</title>
      </Helmet>
      <MainContainer>
        <PageHeader
          title={translate('creditCheckApplication', creditCheckTranslations, preferredLanguage)}
          description={translate('softCreditInquiry', creditCheckTranslations, preferredLanguage)}
        />
        <Box padding={{ base: '0 lg', tablet: '0' }}>
          <Formik
            initialValues={initialValues}
            validationSchema={applicationSchema}
            validateOnChange={true}
            onSubmit={handleSubmit}
            enableReinitialize={true}
          >
            {({ isSubmitting, setFieldValue }) => (
              <EnhancedFormikForm noValidate>
                <FormSection
                  title={translate('language', creditCheckTranslations, preferredLanguage)}
                  description={translate('languageDescription', creditCheckTranslations, preferredLanguage)}
                >
                  <ApplicationPreferredLanguage
                    isSubmitting={isSubmitting}
                    isLoading={isLoading}
                    setPreferredLanguage={setPreferredLanguage}
                  />
                </FormSection>
                <FormSection
                  title={translate('applicantInformation', creditCheckTranslations, preferredLanguage)}
                  description={translate(
                    strictCreditCheckProgramTypes.includes(accountData?.programType as ProgramType)
                      ? 'consumerSolarApplicantDescription'
                      : 'applicantDescription',
                    creditCheckTranslations,
                    preferredLanguage,
                  )}
                >
                  <ApplicantInformation
                    isSubmitting={isSubmitting}
                    isLoading={isLoading}
                    isCreditApproved={isCreditApproved}
                    prefix="primary"
                    isDobRequired={isDobRequiredForProgramType(accountData?.programType)}
                  />
                </FormSection>
                {isCoApplicantAllowedForProgramType(accountData?.programType) && (
                  <FormSection
                    title={translate('additionalApplicant', creditCheckTranslations, preferredLanguage)}
                    description={
                      <Box as="ul" color="body-secondary" padding="xs 0 0 md" childGap="xs">
                        <li>
                          {translate('additionalApplicantDescription', creditCheckTranslations, preferredLanguage)}
                        </li>
                        <li>{translate('differentEmailRequired', creditCheckTranslations, preferredLanguage)}</li>
                        <li>{translate('eitherApplicantQualified', creditCheckTranslations, preferredLanguage)}</li>
                      </Box>
                    }
                  >
                    <Toggle
                      id="addAdditionalApplicant"
                      label={translate('additionalApplicant', creditCheckTranslations, preferredLanguage)}
                      isChecked={showAdditionalApplicant}
                      onChange={() => {
                        setShowAdditionalApplicant(!showAdditionalApplicant);
                        setFieldValue('addAdditionalApplicant', !showAdditionalApplicant);
                      }}
                    />
                    {showAdditionalApplicant && (
                      <ApplicantInformation
                        isSubmitting={isSubmitting}
                        isLoading={isLoading}
                        isCreditApproved={isCreditApproved}
                        prefix="secondary"
                        isDobRequired={isDobRequiredForProgramType(accountData?.programType)}
                      />
                    )}
                  </FormSection>
                )}
                <FormSection title={translate('addressInformation', creditCheckTranslations, preferredLanguage)}>
                  <AddressFormFields isSubmitting={isSubmitting} showAddress2 />
                </FormSection>
                {propertyAddressNeeded && (
                  <FormSection title={translate('propertyAddress', creditCheckTranslations, preferredLanguage)}>
                    <AddressFormFields isSubmitting={isSubmitting} prefix="property" showAddress2 />
                  </FormSection>
                )}
                <FormSection title={translate('disclosures', creditCheckTranslations, preferredLanguage)}>
                  {preApprovedApplication && preApprovedDisclosuresAcceptance ? (
                    <p>{translate('disclosuresAlreadyAccepted', creditCheckTranslations, preferredLanguage)}</p>
                  ) : (
                    disclosures &&
                    disclosures.length > 0 &&
                    disclosures.map((disclosure: any) => (
                      <DisclosureCard
                        disclosure={disclosure}
                        key={disclosure.id}
                        disclosureProperty="creditApplicationDisclosure"
                        isLoading={isLoading}
                      />
                    ))
                  )}
                </FormSection>
                {enableTurnstileOnCreditCheck &&
                  !!inviteToken &&
                  accountData?.programType === ProgramType.consumerSolar && (
                    <Box padding="lg 0 0 0" alignItems={{ tablet: 'flex-end' }}>
                      <TurnstileWidget
                        ref={turnstileRef}
                        onSuccess={handleTurnstileSuccess}
                        onExpire={handleTurnstileExpire}
                        onError={handleTurnstileError}
                      />
                    </Box>
                  )}
                <Box
                  direction={{
                    base: 'column',
                    tablet: 'row',
                  }}
                  alignItems={{
                    base: 'stretch',
                    tablet: 'flex-end',
                  }}
                  justifyContent={{
                    tablet: 'flex-end',
                  }}
                  childGap="sm"
                  style={{ flexShrink: 0 }}
                  padding="lg 0 0 0"
                >
                  <Button
                    as="a"
                    navigate={() => navigate(`/accounts/${id}`)}
                    variant="secondary"
                    tone="neutral"
                    size="md"
                    isDisabled={isLoading}
                    isLoading={isSubmitting}
                  >
                    {translate('cancel', creditCheckTranslations, preferredLanguage)}
                  </Button>
                  <Button
                    size="md"
                    variant="primary"
                    type="submit"
                    isDisabled={
                      isLoading ||
                      !isAllowedToExecuteAction ||
                      (enableTurnstileOnCreditCheck &&
                        !!inviteToken &&
                        accountData?.programType === ProgramType.consumerSolar &&
                        !turnstileToken &&
                        !turnstileError)
                    }
                    isLoading={isSubmitting}
                  >
                    {translate('submit', creditCheckTranslations, preferredLanguage)}
                  </Button>
                </Box>
              </EnhancedFormikForm>
            )}
          </Formik>
          <Modal isOpen={isCreditCheckSuccessModal} onDismiss={() => {}}>
            <Modal.Header
              id="credit-check-success-modal"
              title={translate('creditCheckApprovalModalTitle', creditCheckTranslations, preferredLanguage)}
            />
            <Modal.Body>
              <Box>{translate('creditCheckApprovalModalMessage', creditCheckTranslations, preferredLanguage)}</Box>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="primary" onClick={() => navigate(`/accounts/${id}/system-design`)}>
                {translate('goToSystemDesign', creditCheckTranslations, preferredLanguage)}{' '}
                {Math.max(countdown - 1, 0)}
              </Button>
            </Modal.Footer>
          </Modal>
        </Box>
      </MainContainer>
    </>
  );
}
